import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Inbox, Plus, RadioTower, RefreshCw, Trash2, UserRound } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { api } from '../../api';
import { EmptyState, Modal, PageHeader, StatusBadge, Surface } from '../../components/ui';
import { DeviceHardware } from '../../components/DeviceLogo';

const ONLINE_MS = 2 * 60 * 1000;
const isOnline = (lastSeen) => lastSeen && (Date.now() - new Date(`${lastSeen}Z`).getTime()) < ONLINE_MS;

function DeviceStatus({ device, t }) {
  if (device.status !== 'provisioned') return <StatusBadge tone="warning">{t('adminDevices.statusPending')}</StatusBadge>;
  const online = isOnline(device.last_seen);
  return <StatusBadge tone={online ? 'success' : 'muted'}>{online ? t('adminDevices.statusOnline') : t('adminDevices.statusOffline')}</StatusBadge>;
}

function formatDate(value) {
  return value ? new Date(`${value}Z`).toLocaleString() : '—';
}

export default function AdminDevicesPage() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [devices, setDevices] = useState([]);
  const [filter, setFilter] = useState('all');
  const [loading, setLoading] = useState(true);
  const [showAddModal, setShowAddModal] = useState(false);
  const [newSerial, setNewSerial] = useState('');
  const [newName, setNewName] = useState('');
  const [newNotes, setNewNotes] = useState('');
  const [addError, setAddError] = useState('');
  const [loadError, setLoadError] = useState('');
  const [adding, setAdding] = useState(false);
  const [deleting, setDeleting] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    setLoadError('');
    api.getDevices().then(setDevices).catch((err) => setLoadError(err.message)).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    const timer = setInterval(load, 30_000);
    return () => clearInterval(timer);
  }, [load]);

  const counts = {
    all: devices.length,
    provisioned: devices.filter((device) => device.status === 'provisioned').length,
    registered: devices.filter((device) => device.status === 'registered').length,
  };
  const filtered = filter === 'all' ? devices : devices.filter((device) => device.status === filter);
  const tabs = [
    { key: 'all', label: t('adminDevices.tabAll') },
    { key: 'provisioned', label: t('adminDevices.tabProvisioned') },
    { key: 'registered', label: t('adminDevices.tabPending') },
  ];

  const closeModal = () => { setShowAddModal(false); setAddError(''); };
  const handleAdd = async (event) => {
    event.preventDefault(); setAdding(true); setAddError('');
    try {
      await api.createDevice({ serial_number: newSerial, name: newName || undefined, notes: newNotes || undefined });
      closeModal(); setNewSerial(''); setNewName(''); setNewNotes(''); load();
    } catch (err) { setAddError(err.message); }
    finally { setAdding(false); }
  };
  const handleDelete = async (event, id) => {
    event.stopPropagation();
    if (!window.confirm(t('adminDevices.deleteConfirm'))) return;
    setDeleting(id);
    try { await api.deleteDevice(id); load(); }
    catch (err) { window.alert(err.message); }
    finally { setDeleting(null); }
  };

  return (
    <div className="page">
      <PageHeader icon={RadioTower} title={t('adminDevices.title')} subtitle={t('adminDevices.subtitle')} actions={
        <button type="button" className="btn btn-primary" onClick={() => setShowAddModal(true)}><Plus className="icon" aria-hidden="true" />{t('adminDevices.addDevice')}</button>
      } />
      {loadError && <div className="alert alert-danger" role="alert">{loadError}</div>}

      <Surface>
        <div className="tabs" role="tablist" aria-label={t('adminDevices.title')}>
          {tabs.map(({ key, label }) => <button key={key} type="button" role="tab" aria-selected={filter === key} className={`tab${filter === key ? ' active' : ''}`} onClick={() => setFilter(key)}>{label}<span className="tab-count">{counts[key]}</span></button>)}
          <button type="button" className="icon-btn ms-auto" onClick={load} title={t('adminDevices.refresh')} aria-label={t('adminDevices.refresh')}><RefreshCw className="icon" aria-hidden="true" /></button>
        </div>

        {loading ? <div className="empty-state"><div className="spinner-border" role="status" /></div>
          : filtered.length === 0 ? <EmptyState icon={Inbox} title={filter === 'all' ? t('adminDevices.emptyAll') : t('adminDevices.emptyFiltered')} />
            : <div className="table-responsive"><table className="table table-hover mb-0">
              <thead><tr>{[t('adminDevices.colSerial'), t('adminDevices.colName'), t('adminDevices.colStatus'), t('adminDevices.colOwner'), t('adminDevices.colLastActive'), t('adminDevices.colTransport'), t('adminDevices.colNotes'), ''].map((heading, index) => <th key={`${heading}-${index}`}>{heading}</th>)}</tr></thead>
              <tbody>{filtered.map((device) => <tr key={device.id} role="link" tabIndex={0} onClick={() => navigate(`/admin/devices/${device.id}`)} onKeyDown={(event) => event.key === 'Enter' && navigate(`/admin/devices/${device.id}`)}>
                <td><DeviceHardware device={device} /><code className="d-block small mt-1">{device.serial_number}</code></td>
                <td><span className={device.name ? 'fw-semibold text-strong' : 'text-muted fst-italic'}>{device.name || t('adminDevices.noName')}</span></td>
                <td><DeviceStatus device={device} t={t} /></td>
                <td>{device.owner_username ? <button type="button" className="btn btn-sm btn-outline-primary" onClick={(event) => { event.stopPropagation(); navigate(`/admin/users/${device.owner_id}`); }}><UserRound className="icon" aria-hidden="true" />{device.owner_username}</button> : <span className="text-muted">—</span>}</td>
                <td className="small text-muted text-nowrap">{formatDate(device.last_seen)}</td>
                <td>{device.transport ? <code>{device.transport}</code> : <span className="text-muted">—</span>}</td>
                <td className="small text-muted notes-cell">{device.notes || '—'}</td>
                <td onClick={(event) => event.stopPropagation()}><button type="button" className="icon-btn text-danger" disabled={deleting === device.id} onClick={(event) => handleDelete(event, device.id)} title={t('adminDevices.delete')} aria-label={t('adminDevices.delete')}><Trash2 className="icon" aria-hidden="true" /></button></td>
              </tr>)}</tbody>
            </table></div>}
      </Surface>

      {showAddModal && <Modal title={t('adminDevices.modalTitle')} onClose={closeModal} labelledBy="add-device-title">
        {addError && <div className="alert alert-danger py-2 small" role="alert">{addError}</div>}
        <form onSubmit={handleAdd}>
          <div className="mb-3"><label className="form-label" htmlFor="device-serial">{t('adminDevices.modalSerialLabel')}</label><input id="device-serial" className="form-control" value={newSerial} onChange={(event) => setNewSerial(event.target.value)} placeholder="GAS-XXXX" autoFocus required /></div>
          <div className="mb-3"><label className="form-label" htmlFor="device-name">{t('adminDevices.modalNameLabel')}</label><input id="device-name" className="form-control" value={newName} onChange={(event) => setNewName(event.target.value)} placeholder={t('adminDevices.modalNamePh')} /></div>
          <div className="mb-3"><label className="form-label" htmlFor="device-notes">{t('adminDevices.modalNotesLabel')}</label><textarea id="device-notes" className="form-control" rows="3" value={newNotes} onChange={(event) => setNewNotes(event.target.value)} placeholder={t('adminDevices.modalOptional')} /></div>
          <p className="form-text">{t('adminDevices.tokenHint')}</p>
          <div className="modal-actions"><button type="button" className="btn btn-outline-secondary" onClick={closeModal}>{t('adminDevices.modalCancel')}</button><button type="submit" className="btn btn-primary" disabled={adding}><Plus className="icon" aria-hidden="true" />{adding ? t('adminDevices.modalSubmitting') : t('adminDevices.modalSubmit')}</button></div>
        </form>
      </Modal>}
    </div>
  );
}
